import DashboardComponentHeader from 'components/dashboard/common/DashboardComponentHeader';
import styles from './YourSharedWins.module.css';
import ImageWithText from 'components/common/ImageWithText';
import Bell9 from './../../svg/Bell9.svg';
import { OptionalLink } from 'components/common/OptionalLink';
import { Fragment } from 'react';
import * as Sentry from '@sentry/browser';
import {
  SharedWinForFriendItem,
  SharedWinFromFriendItem,
  SharedWinUnion,
} from 'hooks/referrals/useReferralsSharedWins';

export interface Props {
  sharedWins: SharedWinUnion[];
}

const YourSharedWins = (props: Props) => {
  const unclaimed = props.sharedWins.filter(
    (x) => x.type === 'FRIEND_WIN' && !x.promoPrizeRewardClaimed,
  ).length;
  return (
    <div>
      <DashboardComponentHeader
        title="Your shared wins"
        subtitle={<>All the prizes you've won with friends</>}
        separator
        banner={
          unclaimed > 0 ? (
            <span className={styles.badge}>{unclaimed} UNCLAIMED</span>
          ) : undefined
        }
      />
      <div className={styles.sharedWinsContainer}>
        {props.sharedWins.map((x) =>
          x.type === 'FRIEND_WIN' ? renderFriendWin(x) : renderPlayerWin(x),
        )}
      </div>
    </div>
  );
};

function renderPlayerWin(data: SharedWinForFriendItem) {
  const lastFriend =
    data.otherPlayerFirstNames[data.otherPlayerFirstNames.length - 1];
  const otherFriends = data.otherPlayerFirstNames.slice(0, -1);

  return (
    <ImageWithText
      imageSize="small"
      imageSrc={getImageForTier(data.otherPlayerPrizeTier)}
      textChildren={
        <>
          <strong>
            {otherFriends.join(', ')}
            {otherFriends.length > 0 ? ' and ' : ''}
            {lastFriend} won a {data.otherPlayerPrizeName}
          </strong>
          with your {data.prizeTier} prize
        </>
      }
    />
  );
}

function renderFriendWin(data: SharedWinFromFriendItem) {
  return (
    <OptionalLink
      className={styles.sharedWinLink}
      fallback={Fragment}
      href={data.promoPrizeRewardClaimed ? undefined : '/my-wins'}
    >
      <ImageWithText
        imageSize="small"
        imageSrc={getImageForTier(data.promoPrizeTier)}
        cta={data.promoPrizeRewardClaimed ? undefined : 'chevron'}
        imageOverlayChildren={
          data.promoPrizeRewardClaimed ? undefined : (
            <span className={styles.bell}>
              <Bell9 />
            </span>
          )
        }
        textChildren={
          <>
            <strong>
              {data.promoPrizeRewardClaimed ? 'You won a ' : 'Claim your '}
              {data.promoPrizeRewardName}
            </strong>
            won with {data.otherPlayerFirstName}'s {data.otherPlayerPrizeTier}{' '}
            win
          </>
        }
      />
    </OptionalLink>
  );
}

function getImageForTier(prizeTier: string) {
  switch (prizeTier) {
    case 'WINWITHFRIENDS_TREAT1':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/10k-entries.jpg';
    case 'WINWITHFRIENDS_TREAT2':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/10k-entries.jpg';
    case 'WINWITHFRIENDS_TREAT3':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/tokens.jpg';
    case 'WINWITHFRIENDS_BRONZE':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/bronze.jpg';
    case 'WINWITHFRIENDS_SILVER':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/silver.jpg';
    case 'WINWITHFRIENDS_GOLD':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/gold.jpg';
    case 'WINWITHFRIENDS_PLATINUM':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/platinum.jpg';
    case 'WINWITHFRIENDS_DIAMOND':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/diamond.jpg';
    case 'WINWITHFRIENDS_DAYMAKER':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/daymaker.jpg';
    case 'WINWITHFRIENDS_DAYMAKERBOOST':
      return 'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/referrals/wwf-prizes/daymaker-boost.jpg';
  }

  Sentry.captureMessage(
    'Failed to get YourSharedWins prizeTier image for ' + prizeTier,
    'warning',
  );
  return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
}

export default YourSharedWins;
